import React, { useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { ChevronDown, Menu, X, Megaphone } from "lucide-react";
import { warehouseInventoryService } from "../../services/warehouseInventory.service";
import WarehouseSidebar, {
  WarehouseMobileSidebar,
} from "./components/WarehouseSidebar";
import { AUTH_STATE_EVENT, getAuthUser } from "../AdminAuth/components/authStore";
import { env } from "../../config/env";
import WarehouseNotificationsBell from "./components/WarehouseNotificationsBell";
import DispatchAutoAssignFailurePopup from "../../components/Notifications/DispatchAutoAssignFailurePopup";
import { useOrderNotificationSoundAlert } from "../../hooks/useOrderNotificationSoundAlert";
import warehouseDashboardHero from "../../assets/warehouse-dashboard-hero.png";

function titleFromPath(pathname) {
  if (pathname.startsWith("/admin/dashboard/profile")) return "My Profile";
  if (pathname.startsWith("/admin/dashboard/notifications")) return "Notifications";
  if (pathname.startsWith("/admin/dashboard/employees")) return "Employees";
  if (pathname.startsWith("/admin/dashboard/products")) return "Products";
  if (pathname.startsWith("/admin/dashboard/stock")) return "Stock";
  if (pathname.startsWith("/admin/dashboard/inbound-receipts")) return "Inbound Receipts";
  if (pathname.startsWith("/admin/dashboard/inventory")) return "Inventory";
  if (pathname.startsWith("/admin/dashboard/customer-orders")) return "Customer Orders";
  if (pathname.startsWith("/admin/dashboard/warehouse-sales")) return "Sales Analytics";
  if (pathname.startsWith("/admin/dashboard/payment-collections")) return "Payment Collections";
  if (pathname.startsWith("/admin/dashboard/activity-logs")) return "Activity Logs";
  if (pathname.startsWith("/admin/dashboard/coupons")) return "Coupons";
  return "Warehouse Dashboard";
}

function getDisplayName(user) {
  return (
    user?.name ||
    user?.fullName ||
    user?.adminName ||
    user?.username ||
    user?.email?.split("@")?.[0] ||
    "Admin"
  );
}

function getDisplayRole(user) {
  return String(user?.role || "Admin")
    .replaceAll("_", " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function getProfileImage(user) {
  return (
    user?.onboarding?.documents?.profilePhoto?.url ||
    user?.onboarding?.photos?.photo1?.url ||
    user?.profileImage ||
    user?.profile_image ||
    user?.avatarUrl ||
    user?.image ||
    user?.avatar ||
    ""
  );
}

function toAbsoluteFileUrl(path) {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  const base = env?.AUTH_BASE_URL || "";
  return `${base}${path}`;
}

function toBroadcastImageUrl(path) {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;

  const base = env?.AUTH_BASE_URL || "";

  return `${base}${path.startsWith("/") ? path : `/${path}`}`;
}

function normalizeActionUrl(url) {
  const v = String(url || "").trim();
  if (!v) return "";
  if (v.startsWith("/")) return v;
  if (/^https?:\/\//i.test(v)) return v;
  return `https://${v}`;
}

function getBroadcastSeenKey(item) {
  const id = item?.id || item?.broadcastId || item?._id;
  return id ? `warehouse_broadcast_seen_${id}` : "";
}

function isBroadcastAlreadySeen(item) {
  const key = getBroadcastSeenKey(item);
  if (!key) return false;

  try {
    return localStorage.getItem(key) === "1";
  } catch {
    return false;
  }
}

function markBroadcastSeenLocal(item) {
  const key = getBroadcastSeenKey(item);
  if (!key) return;

  try {
    localStorage.setItem(key, "1");
  } catch {
    // ignore localStorage error
  }
}

function WarehouseBroadcastPopup({ item, onClose }) {
  if (!item) return null;

  const imageUrl = toBroadcastImageUrl(item.imageUrl);

  return (
    <div className="fixed inset-0 z-[1400] flex items-center justify-center bg-slate-900/55 px-4 py-6 backdrop-blur-sm">
      <div className="relative w-full max-w-[520px] overflow-hidden rounded-[28px] border border-[#f2c6cc] bg-white shadow-[0_30px_90px_rgba(216,106,120,0.22)]">
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 z-10 grid h-10 w-10 place-items-center rounded-full bg-white/90 text-slate-500 shadow-sm transition hover:bg-[#fff1f3] hover:text-[#d86a78]"
        >
          <X size={18} />
        </button>

        {imageUrl ? (
          <img
            src={imageUrl}
            alt={item.title || "Broadcast"}
            className="h-[210px] w-full object-cover"
          />
        ) : (
          <div className="grid h-[170px] place-items-center bg-[#fff6f7] text-[#d86a78]">
            <Megaphone size={44} />
          </div>
        )}

        <div className="p-6">
          <div className="inline-flex items-center gap-2 rounded-full bg-[#fff1f3] px-3 py-1.5 text-xs font-bold text-[#d86a78]">
            <Megaphone size={14} />
            Warehouse Broadcast
          </div>

          <h2 className="mt-4 text-[24px] font-bold leading-tight text-slate-900">
            {item.title || "Broadcast Message"}
          </h2>

          <p className="mt-3 whitespace-pre-wrap text-sm leading-6 text-slate-600">
            {item.body || ""}
          </p>
          {item.actionUrl ? (
            <button
              type="button"
             onClick={() => {
  const url = normalizeActionUrl(item.actionUrl);

  if (url) {
    window.open(url, "_blank", "noopener,noreferrer");
  }
}}
              className="mt-4 inline-flex rounded-[12px] bg-[#fff1f3] px-4 py-2 text-sm font-semibold text-[#d86a78] hover:bg-[#ffe6ea]"
            >
              {item.actionUrl}
            </button>
          ) : null}

          <div className="mt-6 flex justify-end">
            {/* <button
              type="button"
              onClick={() => {
                if (item.actionUrl) {
                  window.location.href = item.actionUrl;
                }

                onClose();
              }}
              className="inline-flex h-11 items-center justify-center rounded-[14px] bg-[#d86a78] px-6 text-sm font-semibold text-white transition hover:bg-[#c95b69]"
            >
              Close
            </button> */}
          </div>
        </div>
      </div>
    </div>
  );
}

function ProfileMenu() {
  const [user, setUser] = useState(() => getAuthUser() || {});
  const navigate = useNavigate();

  useEffect(() => {
    const syncUser = () => setUser(getAuthUser() || {});

    window.addEventListener(AUTH_STATE_EVENT, syncUser);
    window.addEventListener("storage", syncUser);

    return () => {
      window.removeEventListener(AUTH_STATE_EVENT, syncUser);
      window.removeEventListener("storage", syncUser);
    };
  }, []);

  const displayName = getDisplayName(user);
  const displayRole = getDisplayRole(user);
  const profileImage = toAbsoluteFileUrl(getProfileImage(user));
  const initial = displayName?.charAt(0)?.toUpperCase() || "A";

  return (
    <div className="relative group">
      <button
        type="button"
        onClick={() => navigate("/admin/dashboard/profile")}
        className="flex h-12 items-center gap-2.5 rounded-[17px] border border-white/90 bg-white/80 px-2.5 shadow-[0_8px_22px_rgba(37,48,78,0.07)] ring-1 ring-slate-200/70 backdrop-blur-md transition-all hover:-translate-y-0.5 hover:border-[#f2c6cc] hover:bg-white sm:h-[54px] sm:px-3"
      >
        <div className="flex h-9 w-9 items-center justify-center overflow-hidden rounded-full border border-[#f2c6cc] bg-[linear-gradient(135deg,#ffe8ed,#ffd3dc)] sm:h-10 sm:w-10">
          {profileImage ? (
            <img
              src={profileImage}
              alt="Profile"
              className="h-full w-full object-cover"
            />
          ) : (
            <span className="text-[15px] font-bold text-[#d86a78]">
              {initial}
            </span>
          )}
        </div>

        <div className="hidden text-left md:block">
          <div className="text-[10px] font-medium text-slate-400">Logged in as</div>
          <div className="max-w-[112px] truncate text-[12px] font-black text-slate-800">
            {displayName}
          </div>
        </div>
        <ChevronDown size={14} className="hidden text-slate-700 transition-transform duration-300 group-hover:rotate-180 md:block" />
      </button>

      <div className="pointer-events-none absolute right-0 top-[calc(100%+10px)] z-50 w-[280px] translate-y-2 rounded-[18px] border border-[#f2c6cc] bg-white p-5 opacity-0 shadow-[0_20px_50px_rgba(0,0,0,0.12)] transition-all duration-200 group-hover:pointer-events-auto group-hover:translate-y-0 group-hover:opacity-100">
        <div className="flex items-center gap-4">
          <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-full border-2 border-[#f2c6cc] bg-[#d86a78]/10">
            {profileImage ? (
              <img
                src={profileImage}
                alt="Profile"
                className="h-full w-full object-cover"
              />
            ) : (
              <span className="text-[22px] font-bold text-[#d86a78]">
                {initial}
              </span>
            )}
          </div>

          <div className="min-w-0">
            <div className="truncate text-[16px] font-bold text-[#d86a78]">
              {displayName}
            </div>
            <div className="mt-1 text-[13px] text-slate-500">{displayRole}</div>
          </div>
        </div>

        <div className="my-4 h-px bg-[#f2c6cc]" />

        <div className="space-y-3 text-sm">
          <div>
            <div className="text-[11px] uppercase text-slate-400">Email</div>
            <div className="truncate text-slate-700">{user?.email || "-"}</div>
          </div>

          <div>
            <div className="text-[11px] uppercase text-slate-400">Role</div>
            <div className="text-slate-700">{displayRole}</div>
          </div>

          <div>
            <div className="text-[11px] uppercase text-slate-400">Status</div>
            <div className="font-medium text-green-600">
              {user?.status || "Active"}
            </div>
          </div>
        </div>

        <div className="mt-5">
          <button
            type="button"
            onClick={() => navigate("/admin/dashboard/profile")}
            className="w-full rounded-[12px] border border-[#f2c6cc] bg-[#fff8f9] px-4 py-2 text-sm font-semibold text-[#d86a78] transition hover:bg-white"
          >
            My Profile
          </button>
        </div>
      </div>
    </div>
  );
}

export default function WarehouseLayout() {
  useOrderNotificationSoundAlert({ source: "warehouse" });

  const location = useLocation();
  const pageTitle = titleFromPath(location.pathname);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem("warehouseSidebarCollapsed") === "1";
  });

  const [broadcastItem, setBroadcastItem] = useState(null);

  const toggleSidebar = () => {
    setSidebarCollapsed((current) => {
      const next = !current;
      try {
        window.localStorage.setItem("warehouseSidebarCollapsed", next ? "1" : "0");
      } catch {}
      return next;
    });
  };

    useEffect(() => {
    let alive = true;

    async function loadBroadcast() {
      try {
        console.log("CALLING WAREHOUSE BROADCAST API");

        const res = await warehouseInventoryService.getActiveWarehouseBroadcast();

        console.log("WAREHOUSE BROADCAST RESPONSE =", res);

        if (!alive) return;

        const item = res?.item || null;

        if (!item) {
          setBroadcastItem(null);
          return;
        }

        // Important: refresh ke baad same broadcast dobara popup nahi hoga
        if (isBroadcastAlreadySeen(item)) {
          setBroadcastItem(null);
          return;
        }

        setBroadcastItem(item);
      } catch (err) {
        console.log("WAREHOUSE BROADCAST ERROR =", err);

        if (alive) {
          setBroadcastItem(null);
        }
      }
    }

    loadBroadcast();

    return () => {
      alive = false;
    };
  }, []);

   async function closeBroadcastPopup() {
    const item = broadcastItem;

    // Pehle local me seen mark karo, taki refresh pe popup repeat na ho
    markBroadcastSeenLocal(item);

    setBroadcastItem(null);

    if (!item?.id) return;

    try {
      await warehouseInventoryService.markWarehouseBroadcastViewed(item.id, {
        source: "close",
      });
    } catch {
      // popup already closed; no need to block user
    }
  }

  return (
    <div className="min-h-screen overflow-x-hidden bg-[#f7f9fc]">
      <DispatchAutoAssignFailurePopup mode="warehouse" />
      <WarehouseBroadcastPopup
        item={broadcastItem}
        onClose={closeBroadcastPopup}
      />
      <WarehouseSidebar collapsed={sidebarCollapsed} onToggleCollapse={toggleSidebar} />
      <WarehouseMobileSidebar
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
      />

      <main
        className={`min-h-screen bg-[radial-gradient(circle_at_75%_0%,rgba(244,114,182,0.06),transparent_22%),linear-gradient(180deg,#f9fbfe_0%,#f6f8fc_100%)] transition-[padding] duration-300 ${
          sidebarCollapsed ? "xl:pl-[78px]" : "xl:pl-[232px]"
        }`}
      >
        <header
          className={`fixed left-0 right-0 top-0 z-30 bg-[#f7f9fc]/90 p-2 backdrop-blur-xl transition-[left] duration-300 sm:p-2.5 ${
            sidebarCollapsed ? "xl:left-[78px]" : "xl:left-[232px]"
          }`}
        >
          <div className="relative flex h-[68px] items-center gap-3 rounded-[22px] border border-white/90 bg-[linear-gradient(105deg,rgba(255,255,255,0.97)_0%,rgba(255,249,250,0.95)_58%,rgba(255,246,240,0.93)_100%)] px-3 shadow-[0_10px_30px_rgba(37,48,78,0.07)] ring-1 ring-slate-200/70 sm:h-[76px] sm:px-4 xl:px-5">
            <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden rounded-[22px]">
              <div className="absolute -right-16 -top-24 h-56 w-56 rounded-full bg-rose-200/20 blur-3xl" />
              <div className="absolute left-[180px] top-[-50px] h-40 w-72 rounded-full bg-[#ffe5eb]/40 blur-3xl" />
              <img
                src={warehouseDashboardHero}
                alt=""
                className="absolute -top-[11px] left-[250px] hidden w-[300px] max-w-none opacity-85 drop-shadow-[0_8px_16px_rgba(80,48,45,0.08)] xl:block 2xl:left-[300px]"
              />
            </div>

            <button
              type="button"
              onClick={() => setMobileOpen(true)}
              className="relative z-10 grid h-11 w-11 shrink-0 place-items-center rounded-[15px] border border-rose-100 bg-white/90 text-[#ee5870] shadow-[0_7px_18px_rgba(216,106,120,0.10)] transition-all hover:-translate-y-0.5 hover:bg-[#fff6f7] sm:h-[52px] sm:w-[52px] xl:hidden"
              aria-label="Open menu"
            >
              <Menu size={20} />
            </button>

            <span className="relative z-10 hidden h-10 w-px bg-slate-200/80 sm:block xl:hidden" />

            <div className="relative z-10 min-w-0">
              <div className="truncate text-[14px] font-black tracking-[-0.01em] text-[#101828] sm:text-[17px]">
                <span className="text-[#d86a78]">{pageTitle}</span>
              </div>
              <div className="hidden text-[11px] font-semibold text-slate-400 sm:block">
                Nefazo Warehouse
              </div>
            </div>

            <div className="relative z-10 ml-auto flex items-center gap-2 sm:gap-3">
              <span className="hidden h-10 w-px bg-slate-200/80 md:block" />
              <WarehouseNotificationsBell />
              <span className="hidden h-10 w-px bg-slate-200/80 md:block" />
              <ProfileMenu />
            </div>
          </div>
        </header>

        <div className="mx-auto w-full max-w-[1920px] px-3 pb-4 pt-[100px] sm:px-5 sm:pb-5 sm:pt-[116px] xl:px-7 xl:pb-6 xl:pt-[120px]">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
